import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import apiClient from '../api/client';
import { useAuth } from './AuthContext';

/**
 * SavedCandidatesContext — keeps the employer's shortlist in sync across the
 * dashboard "Din shortlist" section and the candidate search page (heart
 * icons on each card). The Set is the source of truth; the full list is
 * fetched on mount + refreshed on save/unsave.
 */
const SavedCandidatesContext = createContext({
  savedIds: new Set(),
  candidates: [],
  loading: false,
  toggleSaved: () => Promise.resolve(false),
  refresh: () => Promise.resolve(),
  isSaved: () => false,
});

export const SavedCandidatesProvider = ({ children }) => {
  const { user } = useAuth();
  const [candidates, setCandidates] = useState([]);
  const [savedIds, setSavedIds] = useState(() => new Set());
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user || user.role !== 'employer') return;
    setLoading(true);
    try {
      const res = await apiClient.get('/api/employer/saved-candidates');
      const list = res.data.candidates || [];
      setCandidates(list);
      setSavedIds(new Set(list.map((c) => c.profile_id)));
    } catch (err) {
      // Silent fail — non-employer users won't have access; UI will degrade
      console.warn('[saved-candidates] refresh failed', err?.response?.status);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user?.role === 'employer') refresh();
  }, [user, refresh]);

  const toggleSaved = useCallback(async (profileId) => {
    if (!profileId) return false;

    // Compute wasSaved from the functional setter so we don't capture stale
    // state in the closure (this also keeps the experimental react-hooks
    // immutability lint rule happy).
    let wasSaved = false;
    setSavedIds((prev) => {
      wasSaved = prev.has(profileId);
      const next = new Set(prev);
      if (wasSaved) next.delete(profileId);
      else next.add(profileId);
      return next;
    });

    try {
      if (wasSaved) {
        await apiClient.delete(`/api/employer/saved-candidates/${profileId}`);
        setCandidates((prev) => prev.filter((c) => c.profile_id !== profileId));
        return false;
      }
      const res = await apiClient.post('/api/employer/saved-candidates', { profile_id: profileId });
      const newCandidate = res.data.candidate;
      if (newCandidate) {
        setCandidates((prev) => {
          // Prepend (newest first) and deduplicate
          const filtered = prev.filter((c) => c.profile_id !== profileId);
          return [newCandidate, ...filtered];
        });
      }
      return true;
    } catch (err) {
      // Roll back optimistic change
      console.error('[saved-candidates] toggle failed', err);
      setSavedIds((prev) => {
        const next = new Set(prev);
        if (wasSaved) next.add(profileId);
        else next.delete(profileId);
        return next;
      });
      return wasSaved;
    }
  }, []);

  const isSaved = useCallback((profileId) => savedIds.has(profileId), [savedIds]);

  return (
    <SavedCandidatesContext.Provider value={{ savedIds, candidates, loading, toggleSaved, refresh, isSaved }}>
      {children}
    </SavedCandidatesContext.Provider>
  );
};

export const useSavedCandidates = () => useContext(SavedCandidatesContext);
